import { GearItemJson, LegacyGearItemJson, legacySpentGold } from "./gear";

/**
 * The gear v1 → v2 fold (docs/GEAR_V2.md §Migration; app saves.ts v6 → v7). Frozen here, as a migration must be: a
 * later re-tune of gear.json never changes what an old save becomes.
 *
 * A v1 item had a level requirement R and +N (primary × (1 + 0.05 N)); a v2 item has an item level L (primary ×
 * (1 + 0.01 × max(0, L − 10))). The fold gives every item L = max(R, 10 + 5 N), so its primary is never lower than
 * before (+N alone needs 10 + 5 N; an item already at a higher requirement gets that level's factor on top, a gain).
 * - The +N steps the requirement already covers (10 + 5k ≤ R) bought nothing the level does not give: their gold
 *   (their share of the item's recorded spend, step k having cost k × the base) is refunded in gold.
 * - The levels above R are counted as raised with essence (`raised`, `essenceSpent` = what they cost in essence at the
 *   v2 rates frozen here), so salvage or a sale later gives back the usual share, and the bind rule sees them.
 */
export const GEAR_FOLD = {
  flatThrough: 10,
  levelsPerPlus: 5,
  /** gear.json essence.raise and raiseLevelDivisor as of the fold (2026-10-05). */
  raise: [2, 3, 4, 6, 9, 12] as readonly number[],
  raiseLevelDivisor: 10,
} as const;

export interface GearFold {
  readonly item: GearItemJson;
  /** Gold given back for +N steps the item level already covers. */
  readonly refundGold: number;
}

const int = (v: unknown, dflt: number): number => (typeof v === "number" && Number.isFinite(v) ? Math.floor(v) : dflt);

/** Folds one v1 item into its v2 shape (already-v2 input — it has `level` — comes back as it is, with no refund). */
export function foldLegacyGear(j: LegacyGearItemJson | GearItemJson): GearFold {
  if ("level" in j && typeof j.level === "number") return { item: j as GearItemJson, refundGold: 0 };
  const v1 = j as LegacyGearItemJson;
  const F = GEAR_FOLD;
  const req = Math.max(1, int(v1.levelReq, 1));
  const plus = Math.max(0, Math.min(int(v1.plus, 0), 99));
  const spent = legacySpentGold(v1.upgradeSpent);
  const byPlus = plus > 0 ? F.flatThrough + F.levelsPerPlus * plus : 0;
  const level = Math.max(req, byPlus);
  // +k is covered by the requirement when 10 + 5k ≤ R; the first `covered` steps cost k × base each.
  const covered = plus === 0 ? 0 : Math.min(plus, Math.max(0, Math.floor((req - F.flatThrough) / F.levelsPerPlus)));
  const refundGold = plus === 0 ? spent : Math.floor((spent * covered * (covered + 1)) / (plus * (plus + 1)));
  const raised = level - req;
  const rarity = Math.max(0, Math.min(int(v1.rarity, 0), F.raise.length - 1));
  let essenceSpent = 0;
  for (let l = req; l < level; l++) essenceSpent += Math.ceil((F.raise[rarity] * (F.raiseLevelDivisor + l)) / F.raiseLevelDivisor);
  const { levelReq: _r, plus: _p, upgradeSpent: _u, ...rest } = v1;
  return { item: { ...rest, level, raised, essenceSpent }, refundGold };
}
